// @ts-nocheck
import React, { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ResultMessage } from '@r1-frontend/unified-address-form';
import { flush } from '@r1-frontend/unified-address-form/actions';
import { useCtxDispatch } from '@r1-frontend/unified-address-form/hooks';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { Location } from '@r1-frontend/ui-react/components/svg/location';
import { InfoCircle } from '@r1-frontend/ui-react/components/svg/main';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { IndentContainer, ListContainer, RounderContainer } from '@r1-frontend/ui-react/experimental/containers';
import ListItemWithIcon from '@r1-frontend/ui-react/organisms/ListItemWithIcon';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { POLICY_PAGE_LINK } from '~/src/constants';
import RequestForm from '~/src/features/full-buy/AddressCheckMessage/RequestForm';
import { FullBuyContext, TFullBuyContextProperties, useAnalytics } from '~/src/features/full-buy/analytics/context';
import { OneLineAddressInput } from '~/src/features/full-buy/components/OneLineAddressInput/OneLineAddressInput';
import isFullBuyVillage from '~/src/features/full-buy/helpers/ab/isFullBuyVillage';
import { setAddress } from '~/src/features/full-buy/store/actions';
import { ETariffFilters } from '~/src/features/full-buy/store/initialState';
import { TState } from '~/src/store';

import * as FullBuyST from '~/src/features/full-buy/AddressCheckMessage/styled';
import * as S from './styled';

const SPEED_OF_INTERNET_100 = 100;

const UnifiedAddressForm = ({ ...props }): JSX.Element => {
    const analytics = useAnalytics();
    const dispatch = useDispatch();
    const { isVillage } = useContext<TFullBuyContextProperties>(FullBuyContext);

    const checkingResult = useSelector((state: TState) => state.fullBuy?.selectedAddress?.checkingResult);
    const addressFormDispatch = useCtxDispatch();
    const buttonText = useMemo(() => 'Проверить адрес', []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [checkingResult]);


    const handleChangeAddress = () => {
        analytics.address.changeAddressClicked();
        dispatch(setAddress(undefined));
        addressFormDispatch(flush());
    };

    const goToMainPageWithRestrictions = () => {
        // @todo remove ignore, write type
        // @ts-ignore
        const restrictions = checkingResult.availableSpeed.max;

        if (restrictions === SPEED_OF_INTERNET_100) {
            analytics.address.only100Clicked();
        } else {
            analytics.address.lessThan1000Clicked();
        }

        location.href = `/full-buy?filters=${restrictions === SPEED_OF_INTERNET_100 ? ETariffFilters.SPEED100 : ETariffFilters.SPEED_LESS_1000}${isVillage ? '&filters=cottage' : ''}`;
    };

    const goToMainPageInViewOfCompatibilities = () => {
        location.href = '/full-buy' + (isVillage ? '' : '?filters=cottage');
    };

    // main component with the address input form and the own store
    if (!checkingResult) {
        return <ListContainer $direction={'row'} $desktop={24} $mobile={16} {...props}>
            <ResultMessage/>
            <RounderContainer $desktop={24} $mobile={16}>
                <IndentContainer $desktop={[32]} $mobile={[24]}>
                    <OneLineAddressInput/>
                    <IndentContainer $united={[12, 0, 0]}>
                        <PolicyNote agreeUrl={POLICY_PAGE_LINK} buttonText={buttonText}/>
                    </IndentContainer>
                </IndentContainer>
            </RounderContainer>
        </ListContainer>;
    }

    // situation when checking was fail or connection unavailable
    if (checkingResult && checkingResult.isConnectionAvailable === false) {
        return <ListContainer $direction={'row'} $desktop={24} $mobile={16}>
            <FullBuyST.Alert Icon={InfoCircle} type={'error'}>
                <Paragraph4 dangerouslySetInnerHTML={{ __html: checkingResult.message }}/>
                <RequestForm/>
            </FullBuyST.Alert>

            <S.GrayBlock $desktop={24} $mobile={16}>
                <ListItemWithIcon Icon={Location} title={'Адрес подключения'} value={checkingResult.address.value}/>
                <Button bType={'secondary'} size={'medium'} onClick={handleChangeAddress}>Изменить</Button>
            </S.GrayBlock>
        </ListContainer>;
    }

    // connection available
    return <S.Block>
        <ListContainer $direction={'column'} $desktop={24} $mobile={16}>
            {checkingResult.isCompatible && (
                <ResultMessage/>
            )}

            {(!checkingResult.isTariffAvailableForActivation && checkingResult.isCompatible) && (
                <ButtonGroup>
                    <Button onClick={goToMainPageWithRestrictions}>
                        {checkingResult.availableSpeed.max === 100 && 'Все тарифы 100 Мбит/с'}
                        {checkingResult.availableSpeed.max === 999 && 'Все тарифы до 1000 Мбит/с'}
                    </Button>
                </ButtonGroup>
            )}

            {!checkingResult.isCompatible && (
                <>
                    <FullBuyST.Alert Icon={InfoCircle} type={'error'}>
                        <Paragraph4 dangerouslySetInnerHTML={{ __html: checkingResult.message }}/>
                        <RequestForm/>
                    </FullBuyST.Alert>
                    {isFullBuyVillage() && (
                        <ButtonGroup>
                            <Button onClick={goToMainPageInViewOfCompatibilities}>
                                Выбрать другой тариф
                            </Button>
                        </ButtonGroup>
                    )}
                </>
            )}

            <S.GrayBlock $desktop={24} $mobile={16}>
                <ListItemWithIcon Icon={Location} title={'Адрес подключения'} value={checkingResult.address.value}/>
                <Button bType={'secondary'} size={'medium'} onClick={handleChangeAddress}>Изменить</Button>
            </S.GrayBlock>
        </ListContainer>
    </S.Block>;
};

export default styled(UnifiedAddressForm)`
    ${RounderContainer} {
        background-color: ${COLORS.BgSurface};
        width: 100%;
        display: block;

        overflow: visible;
    }
`;
